import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { i18n } from './i18n';
import { useOrgId } from './org';
import { useCompanySettings } from './settings';

/**
 * Classes, locations, tags and projects (document 06 L1–L4, PR1–PR2; migration 1700). Classes
 * and locations appear on forms only when Company settings › Advanced › Categories switches them
 * on; projects when Advanced › Projects is on; tags always.
 */
export type TreeKind = 'classes' | 'locations';

/** "Not specified" in a report's class, location, project or tag filter (the API's nil UUID). */
export const NONE_ID = '00000000-0000-0000-0000-000000000000';

export interface TreeItem {
  id: string;
  name: string;
  /** "Parent:Child" for a sub-class or sub-location. */
  fullName: string;
  parentId: string | null;
  active: boolean;
  inUse: boolean;
}

export interface Tag {
  id: string;
  name: string;
  groupId: string | null;
  groupName: string | null;
  active: boolean;
  uses: number;
}

export interface TagRef {
  id: string;
  name: string;
  groupId: string | null;
  groupName: string | null;
}

export interface TagGroup {
  id: string;
  name: string;
  tags: Tag[];
}

export type ProjectStatus = 'in_progress' | 'completed' | 'cancelled';

/** A project status in the reader's language. */
export function projectStatusLabel(s: ProjectStatus): string {
  return i18n.t(`settingsPages.data.tracking.projectStatus.${s}`);
}

/**
 * The company's location label in the reader's language: its own wording as typed, or
 * "Location" translated when it kept the default.
 */
export function locationLabelText(label: string): string {
  return label === 'Location' ? i18n.t('settingsPages.data.tracking.location') : label;
}

export interface Project {
  id: string;
  name: string;
  customerId: string;
  customerName: string;
  status: ProjectStatus;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
  income: string;
  costs: string;
  profit: string;
  openBalance: string;
  currency: string;
}

export interface ProjectTransaction {
  entryId: string;
  date: string;
  sourceType: string;
  sourceId: string | null;
  documentId: string | null;
  number: string | null;
  memo: string | null;
  contactName: string | null;
  amount: string;
}

/** What a transaction carries: one location, a class (per transaction), a project and tags. */
export interface TrackingValue {
  classId: string | null;
  locationId: string | null;
  projectId: string | null;
  tagIds: string[];
}

export const EMPTY_TRACKING: TrackingValue = {
  classId: null,
  locationId: null,
  projectId: null,
  tagIds: [],
};

/** The tracking of a saved document, journal entry or bank line as a form value. */
export function trackingFrom(
  x:
    | {
        classId?: string | null;
        locationId?: string | null;
        projectId?: string | null;
        tags?: TagRef[];
      }
    | null
    | undefined,
): TrackingValue {
  return {
    classId: x?.classId ?? null,
    locationId: x?.locationId ?? null,
    projectId: x?.projectId ?? null,
    tagIds: x?.tags?.map((t) => t.id) ?? [],
  };
}

export type TrackClasses = 'off' | 'per_line' | 'per_transaction';

export interface TrackingSettings {
  classes: TrackClasses;
  locations: boolean;
  locationLabel: string;
  projects: boolean;
}

/** Which tracking the open client uses (Company settings › Advanced). */
export function useTrackingSettings(): TrackingSettings {
  const settings = useCompanySettings();
  const a = settings.data?.advanced;
  return {
    classes: a?.trackClasses ?? 'off',
    locations: a?.trackLocations ?? false,
    locationLabel: a?.locationLabel ?? 'Location',
    projects: a?.useProjects ?? false,
  };
}

export function useTree(kind: TreeKind, status: 'active' | 'inactive' | 'all' = 'active') {
  const orgId = useOrgId();
  return useQuery({
    queryKey: [kind, orgId, status],
    queryFn: () => api<TreeItem[]>(`/orgs/${orgId}/${kind}?status=${status}`),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });
}

export function useTagGroups(status: 'active' | 'inactive' | 'all' = 'active') {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['tag-groups', orgId, status],
    queryFn: () =>
      api<{ groups: TagGroup[]; ungrouped: Tag[] }>(`/orgs/${orgId}/tag-groups?status=${status}`),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });
}

export function useProjects(
  opts: { status?: ProjectStatus | 'all'; customerId?: string | null } = {},
) {
  const orgId = useOrgId();
  const status = opts.status ?? 'all';
  const q = new URLSearchParams({ status });
  if (opts.customerId) q.set('customerId', opts.customerId);
  return useQuery({
    queryKey: ['projects', orgId, status, opts.customerId ?? null],
    queryFn: () => api<Project[]>(`/orgs/${orgId}/projects?${q.toString()}`),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });
}

export function useProject(id: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['project', orgId, id],
    queryFn: () => api<Project>(`/orgs/${orgId}/projects/${id}`),
    enabled: Boolean(orgId),
  });
}

export function useProjectTransactions(id: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['project-transactions', orgId, id],
    queryFn: () => api<ProjectTransaction[]>(`/orgs/${orgId}/projects/${id}/transactions`),
    enabled: Boolean(orgId),
  });
}

/** Any change to a tracking list: refreshes every list and the forms' pickers. */
export function useTrackingMutation<T, V>(fn: (orgId: string, v: V) => Promise<T>) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(orgId!, v),
    onSuccess: async () => {
      await Promise.all(
        ['classes', 'locations', 'tag-groups', 'projects', 'project'].map((k) =>
          qc.invalidateQueries({ queryKey: [k, orgId] }),
        ),
      );
    },
  });
}

/** Saves the tags of a posted transaction (tags never touch the ledger). */
export function saveTags(
  orgId: string,
  target: { kind: 'document' | 'journal_entry' | 'payment'; id: string },
  tagIds: string[],
): Promise<TagRef[]> {
  return api<TagRef[]>(`/orgs/${orgId}/tag-links`, {
    method: 'PUT',
    body: JSON.stringify({ target, tagIds }),
  });
}

/** The fields a document or journal body sends; nothing when no tracking is in use. */
export function trackingBody(
  v: TrackingValue,
  s: TrackingSettings,
): Partial<Record<'classId' | 'locationId' | 'projectId', string | null>> & {
  tagIds: string[];
} {
  return {
    ...(s.locations ? { locationId: v.locationId } : {}),
    ...(s.classes === 'per_transaction' ? { classId: v.classId } : {}),
    ...(s.projects ? { projectId: v.projectId } : {}),
    tagIds: v.tagIds,
  };
}
